import { IdRangeEntity } from 'interfaces'
import { DEFAULT_PROP_VALUE } from 'constants/peergroups'
import { AreaType } from 'interfaces/locations'
import * as numeral from 'numeral'
import * as escapeRegExp from 'lodash/escapeRegExp'
import clamp from 'lodash/clamp'
import round from 'lodash/round'
import { HousingType } from 'interfaces/property'
import { FullMoneyflowType } from 'interfaces/moneyflow'
import { FORM_EDIT_INCOME_TRANSACTION, FORM_EDIT_SPENDING_TRANSACTION, FORM_EDIT_TRANSACTIONS_TRANSACTION } from 'constants/forms'
import { CurrencyRoundFormat, MarkdownElement, MarkdownLinkElement, MarkdownTextElement } from 'interfaces/format'
import { RewardAmountEx } from 'interfaces/bounty'

const TEXTAREA_NODE = document.createElement('textarea')

export function decodeHtmlEntities (html: string): string {
  TEXTAREA_NODE.innerHTML = html
  return TEXTAREA_NODE.value
}

numeral.register('format', 'shorten', {
  regexps: {
    format: /0A/,
    unformat: /0A/,
  },
  format: (value) => {
    // >= 100M - XXX M
    if (value >= 1e8) {
      return `${ Math.round(value / 1e6) }M`
    }

    // >= 1M XX.XM
    if (value >= 1e6) {
      const rounded = Math.round(value / 1e5)
      return `${ rounded / 10 }M`
    }

    // >= 100K XXXK
    if (value >= 1e5) {
      return `${ Math.round(value / 1e3) }K`
    }

    // >= 1K XX.XK
    if (value >= 1e3) {
      const rounded = Math.round(value / 100)
      return `${ rounded / 10 }K`
    }

    return String(value)
  },
  unformat: (stringValue) => {
    const value = (numeral as any)._.stringToNumber(stringValue)
    const match = stringValue.match(/\d+(?:\.\d*)?([MK]{1})/)
    if (match) {
      const multiplier = match[1] === 'M' ? 1e6 : 1e3
      return value * multiplier
    }

    return value
  },
})

type DecimalMode =
  | 'no'              // x < 1 shown as 1%
  | 'one'             // x < 1 shown as 0.N%, but if Math.round(x) < 0.1% then use 0%
  | 'one-if-ceiled'   // x < 1 shown as 0.N%, but if Math.round(x) < 0.5% then use 0%
  | 'as-is' // every time it has a fractional part 88.211% => 88.2%, 88.51% => 88.5%, 88% => 88% it will be shown

/**
 * True if x should have decimal with passed mode
 * @param x
 */
const shouldPercentHaveDecimal = (x: number, mode: DecimalMode) => {
  if (mode === 'no') {
    return false
  }
  if (mode === 'as-is') {
    return Math.floor(x * 10000) !== Math.ceil(x * 10000)
  }
  if (mode === 'one-if-ceiled') {
    return Math.round(x * 1000) < 10 && Math.round(x * 100) >= 1
  }

  const valueMult1000 = x * 1000
  return Math.round(valueMult1000) < 10 && Math.round(valueMult1000) >= 1
}

/**
 * Round percent-number to be consistent with its formatting
 * @param x
 */
export const roundPercent = (x: number, mode: DecimalMode) => {
  const precision = shouldPercentHaveDecimal(x, mode) ? 3 : 2
  return round(x, precision)
}

/**
 * Format value to percent
 * @param x
 * @param oneDecimal If we need show first sign after dot when < 1%
 */
export const formatPercent = (x: number, mode: DecimalMode) => {
  const formatString = shouldPercentHaveDecimal(x, mode) ? `0.0%` : `0%`
  return numeral(x).format(formatString)
}

/**
 * Format value to percent
 * @param x
 * @param oneDecimal If we need show first sign after dot when < 1%
 */
export const formatPercentWithHundredths = (x: number): string => {
  const fractionalPart = Math.abs(x - Math.floor(x))

  if (!fractionalPart || fractionalPart < 0.01) {
    return numeral(x).format('0 %')
  }
  return numeral(x).format('0.00 %')
}

export const formatUSCurrency = (amount: number | string, format: CurrencyRoundFormat = 'dollar') => {
  const numeralFormat = format === 'penny'
    ? '$0,0.00'
    : '$0,0'

  return numeral(amount)
    .format(numeralFormat)
}

export function formatAgeRange (age: IdRangeEntity) {
  const upperAge = age.value.upper ? ` \u2013 ${ age.value.upper }` : '+'
  return `${ age.value.lower }${ upperAge }`
}

export function formatCreditRange (credit: IdRangeEntity) {
  const upperScore = credit.value.upper ? ` \u2013 ${ credit.value.upper }` : '+'
  return !!credit.value.lower ? `${ credit.value.lower }${ upperScore }` : `Below ${ credit.value.upper + 1 }`
}

export const formatHousingType = (value: HousingType | null, defaultValue: string = DEFAULT_PROP_VALUE): string => {
  switch (value) {
    case 'ownership':
      return 'Own Home'

    case 'rent':
      return 'Pay Rent'

    case 'other':
      return 'Other'

    default:
      return defaultValue
  }
}

export const formatAreaType = (area_type: AreaType): string => {
  if (area_type === 'urban') {
    return 'Urban'
  } else if (area_type === 'suburban') {
    return 'Suburban'
  } else if (area_type === 'rural') {
    return 'Rural'
  }

  return DEFAULT_PROP_VALUE
}

export const formatRank = (rank: number, isInverted = false) => {
  if (rank > 50) {
    return `${ isInverted ? 'Bottom' : 'Top' } ${ 101 - rank }%`
  }

  return `${ isInverted ? 'Top' : 'Bottom' } ${ rank }%`
}

export const formatAmountDelta = (value: number | string) => {
  const currencyValue = formatUSCurrency(value)

  return value >= 0 ? `+${ currencyValue }` : currencyValue
}

export const toPercent = (x: number) => `${ Math.round(x * 100) / 100 }%`

export const shortenNumber = (x: number): string => numeral(x).format('0A')

export const shortenMoney = (amount: number): string =>
  `$${ shortenNumber(amount) }`

const BREAKDOWN_MAX_MONEY_LENGTH = 6
export const formatBreakdownMoney = (amount: number): string => {
  const roundAmount = Math.round(amount)
  const length = String(roundAmount).length
  if (length <= BREAKDOWN_MAX_MONEY_LENGTH) {
    return numeral(roundAmount).format(`$0,0`)
  }

  const overlap = Math.min(Math.ceil((length - BREAKDOWN_MAX_MONEY_LENGTH) / 3) * 3, 9)
  const shortenAmount = Math.round(roundAmount / 10 ** overlap)
  const letter = overlap === 3
    ? 'K'
    : (
      overlap === 6 ? 'M' : 'B'
    )

  return numeral(shortenAmount).format(`$0,0`) + letter
}

export function shortenMoneyRange (income: IdRangeEntity) {
  const formattedLower = shortenMoney(income.value.lower)
  const formattedUpper = shortenMoney(income.value.upper)
  if (!income.value.upper) {
    return `Above ${ formattedLower }`
  }
  if (!income.value.lower) {
    return `Below ${ formattedUpper }`
  }
  return `${ formattedLower } \u2013 ${ formattedUpper }`
}

export const getMax = (values: (number | null | undefined)[]) => {
  const existingValues = values.filter((value) => !isNaN(value as number) && value !== null) as number[]
  return Math.max(...existingValues)
}

export const formatPhone = (phonenum: string) => {
  const regexObj = /^(?:\+?1[-. ]?)?(?:\(?([0-9]{3})\)?[-. ]?)?([0-9]{3})[-. ]?([0-9]{4})$/
  if (!regexObj.test(phonenum)) {
    return phonenum
  }

  const parts = phonenum.match(regexObj)
  if (!parts) {
    return phonenum
  }

  let phone = ''
  if (parts[1]) {
    phone += '(' + parts[1] + ')'
  }
  phone += parts[2] + '-' + parts[3]

  return phone
}

export const rgbToHex = (rgbString: string) => {
  const parsedColor = parseRgbColor(rgbString)
  if (!parsedColor) {
    return ''
  }

  return ['r' as const, 'g' as const, 'b' as const]
    .map((key) => parsedColor[key].toString(16).padStart(2, '0'))
    .join('')
}

export type RGBColor = {
  r: number
  g: number
  b: number
}

export const parseRgbColor = (rgbString: string): RGBColor | null => {
  const parts = rgbString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
  if (!parts) {
    return null
  }

  return {
    r: parseInt(parts[1], 10),
    g: parseInt(parts[2], 10),
    b: parseInt(parts[3], 10),
  }
}

export const rgbToString = (color: RGBColor): string => {
  const { r, g, b } = color
  return `rgb(${ clamp(r, 0, 255) },${ clamp(g, 0, 255) },${ clamp(b, 0, 255) })`
}

export type TextMatchChunk = {
  chunk: string
  isMatch: boolean
}

/**
 * Should split text 'A b a b c' into array ['A', ' b', 'a', ' b c']
 */
export const getTextMatches = (text: string, entry: string): TextMatchChunk[] => {
  if (!entry) {
    return [
      {
        chunk: text,
        isMatch: false,
      },
    ]
  }

  return text.split(new RegExp(`(${ escapeRegExp(entry) })`, 'gi'))
    .filter((chunk) => chunk.length > 0)
    .map((chunk) => ({
      chunk,
      isMatch: chunk.toLowerCase() === entry.toLowerCase(),
    }))
}

type TransactionEditForms = typeof FORM_EDIT_SPENDING_TRANSACTION | typeof FORM_EDIT_INCOME_TRANSACTION | typeof FORM_EDIT_TRANSACTIONS_TRANSACTION
const formNameMap = {
  spending: FORM_EDIT_SPENDING_TRANSACTION,
  income: FORM_EDIT_INCOME_TRANSACTION,
  transactions: FORM_EDIT_TRANSACTIONS_TRANSACTION,
}
export const getEditTransactionFormName = (type: FullMoneyflowType): TransactionEditForms => formNameMap[type]

type ParseMarkdownLinkArg = {
  captured: string
  label: string
  expression: string
}
const parseMarkdownLink = ({ label, expression }: ParseMarkdownLinkArg): MarkdownLinkElement => {
  return {
    type: 'link',
    label: label,
    expression,
    colonTokens: expression.split(':'),
  }
}

const parseMarkdownText = (captured: string): MarkdownTextElement => {
  return {
    type: 'text',
    text: captured,
  }
}

const MARKDOWN_LINK_RE = '\\[([^\\]]*)\\]\\(([^(]+)\\)'
const PARSE_MARKDOWN_RE = new RegExp(`(${ MARKDOWN_LINK_RE })`, 'gmi')

export const parseMarkdown = (text: string): MarkdownElement[] => {
  const markdownElements: MarkdownElement[] = []
  let foundElementIndex = 0

  const markdownRegexReplacer = (
    _match: string,
    link: string,
    linkLabel: string,
    linkExpression: string,
    offset: number,
    str: string,
  ) => {
    if (offset > foundElementIndex) {
      markdownElements.push(parseMarkdownText(str.substring(foundElementIndex, offset)))
    }

    if (link) {
      markdownElements.push(
        parseMarkdownLink({
          captured: link,
          label: linkLabel,
          expression: linkExpression,
        })
      )
      foundElementIndex += offset + link.length
    }

    return ''
  }

  text.replace(PARSE_MARKDOWN_RE, markdownRegexReplacer)

  if (foundElementIndex < text.length) {
    markdownElements.push(parseMarkdownText(text.substr(foundElementIndex)))
  }

  return markdownElements
}

const END_OF_WORD_REGEXP = /^(.*?)[\s.?!].*$/

export const cutContent = (content = '', maxContentLength = 0, permissibleError = 0): string => {
  if (content.length <= maxContentLength + permissibleError) {
    return content
  } else {
    const tail = content.substr(maxContentLength)
    const tailBeginning = tail.replace(END_OF_WORD_REGEXP, '$1')
    const contentBeginning = content.substr(0, maxContentLength)
    if (tailBeginning.length > permissibleError) {
      return contentBeginning + '…'
    } else {
      return contentBeginning + tailBeginning + '…'
    }
  }
}

export const formatCount = (count?: number) => {
  if (!count) {
    return ''
  }
  if (count > 99) {
    return '99+'
  }
  return String(count)
}

const PERCENTAGE_REWARD_FORMAT = '0.[00]'

export const formatRewardAmount = (amountEx: RewardAmountEx): string => {
  const { value, type } = amountEx

  return type === 'currency'
    ? formatUSCurrency(value, 'penny')
    : `${ numeral(value).format(PERCENTAGE_REWARD_FORMAT) }%`
}

export const createPluralFormatter = (singular: string, plural: string) => {
  return function formatPlural (count: number) {
    return count === 1
      ? singular
      : plural
  }
}

export const capitalizeFirstLetter = (str: string) => {
  const result = str.toLowerCase()
  return result.charAt(0).toUpperCase() + result.slice(1)
}

export const formatMastercard = (str: string) => {
  return str.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim() // Put a space every 4 characters
}

export const toCamelCase = (str: string) => (
  str.replace(
    /([-_][a-z])/ig,
    (firstChar) => firstChar.toUpperCase().replace('-', '').replace('_', '')
  )
)
